import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTenant } from './TenantContext';
import { useTagCategories } from '../hooks/useTagCategories';
import { Course, Enrollment, Tag } from '../types';
import {
  ScopeAxis,
  ScopeSelection,
  findScopeTag,
  inferScopeFromEnrollments,
  resolveScopeAxes,
  resolveScopeValue,
} from '../lib/catalogScope';

/**
 * Catalogue scope — app-wide, not per page.
 *
 * Curriculum scopes everything a student sees (Home, catalogue, course pages,
 * gradebook), so the choice can't live in `CourseCatalog`. It lives here, above
 * `CourseProvider`, which applies it to the course list every student surface
 * reads. Staff are exempt: `CourseContext` skips the filter for instructors and
 * admins, who would otherwise lose sight of courses they own — the same trap
 * the bundle shelf rule documents about `CourseContext.courses`.
 *
 * Where the choice comes from, in order:
 *   1. an explicit choice, remembered per tenant in localStorage
 *   2. a `?curriculum=…` param on arrival (a shared link)
 *   3. what their enrolments already say — someone with four IEB courses has
 *      answered the question, and asking again asks what we know
 *   4. the tenant default (`branding.catalog.scopeDefault`)
 *
 * The URL param is read on ARRIVAL and absorbed, not maintained: with the scope
 * applying to every page, trailing `?curriculum=` on all of them would be noise,
 * and two sources of truth for one setting is how they drift.
 *
 * Persistence is per device (localStorage), matching `dev_tenant_slug`. Moving
 * it to the account is a column on `users` and a change to this file only —
 * nothing else reads the storage key.
 */

interface ScopeContextValue {
  /** Configured axes, outermost first. Empty = the tenant is unscoped. */
  axes: ScopeAxis[];
  /** Chosen value per axis, keyed by tag-category id. */
  selection: ScopeSelection;
  /** True while tag categories are still loading on a scoped tenant. */
  isResolving: boolean;
  /**
   * Axes the student has never answered: nothing chosen, nothing arrived on a
   * link, and their enrolments (once consulted) said nothing either. They are
   * sitting on the tenant default without knowing it. Empty until BOTH sources
   * have been read, so nothing is asked before the answer could be known.
   */
  undecidedAxes: ScopeAxis[];
  setScopeValue: (categoryId: string, tag: Tag) => void;
  /**
   * Read `?curriculum=…` off a query string reached by IN-APP navigation. The
   * arrival read below only sees the URL the page loaded on; a link clicked
   * inside the app (the IEB page's "Sign up free" → /signup?curriculum=IEB)
   * never reloads, so without this the parameter was carried and ignored.
   * Called by ScopeUrlSync, which lives inside the router and can see moves.
   */
  absorbSearch: (search: string) => void;
  /**
   * Offer the student's enrolments as a signal. Applied only when they have
   * made no explicit choice, and only once per session — a later enrolment in
   * the other curriculum must never silently move somebody.
   */
  noteEnrollmentSignal: (courses: Course[], enrollments: Enrollment[]) => void;
}

const ScopeContext = createContext<ScopeContextValue>({
  axes: [],
  selection: {},
  isResolving: false,
  undecidedAxes: [],
  setScopeValue: () => {},
  absorbSearch: () => {},
  noteEnrollmentSignal: () => {},
});

const storageKey = (tenantId: string, categoryId: string) =>
  `kula.scope.${tenantId}.${categoryId}`;

const readStored = (tenantId: string | null, categoryId: string): string | null => {
  if (!tenantId) return null;
  try {
    return window.localStorage.getItem(storageKey(tenantId, categoryId));
  } catch {
    // Private mode, or site data blocked. An unremembered scope is a working
    // scope — it just falls through to the default every visit.
    return null;
  }
};

const writeStored = (tenantId: string | null, categoryId: string, value: string) => {
  if (!tenantId) return;
  try {
    window.localStorage.setItem(storageKey(tenantId, categoryId), value);
  } catch {
    /* not remembering is survivable; failing to render is not */
  }
};

export const ScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenant, tenantId } = useTenant();

  const scoped = !!tenant?.branding?.catalog?.scopeBy?.length;
  const { categories, isLoading } = useTagCategories(tenantId, scoped);

  const axes = useMemo(
    () => resolveScopeAxes(tenant?.branding?.catalog, categories),
    [tenant?.branding?.catalog, categories],
  );

  // Two maps, deliberately NOT one.
  //
  // `explicit` is what the student chose (or a link chose for them); `inferred`
  // is what their enrolments imply. They were one map once, and it was a real
  // bug: CourseProvider is a CHILD of this provider, React flushes passive
  // effects children-first, so on the commit where tag categories land the
  // child's inference wrote a value and this provider's seeding effect — which
  // re-runs on the same `axes` change — replaced the whole map a moment later
  // with an empty one. The inference latch meant it never ran again, so a
  // student with four IEB enrolments silently landed on the CAPS default. It
  // only reproduced on a cold tag-category cache, i.e. the first load of a
  // session, which made it look intermittent.
  //
  // Kept apart, the two sources can't overwrite each other and precedence is
  // stated once, in resolveScopeValue.
  const [explicit, setExplicit] = useState<Record<string, string>>({});
  const [inferred, setInferred] = useState<Record<string, string>>({});
  const inferredFor = useRef<string | null>(null);
  // The two "have we looked yet" flags behind `undecidedAxes`. `seeded` = storage
  // and the URL have been read; `signalSettled` = the enrolments have been
  // consulted, even when there were none to consult. Asking "which curriculum?"
  // before either would ask a question we might be about to answer ourselves.
  const [seeded, setSeeded] = useState(false);
  const [signalSettled, setSignalSettled] = useState(false);

  // Seed from storage, then let an inbound ?curriculum= link override it. Both
  // run once per tenant/axis set rather than on every render, so a student
  // changing the scope isn't fighting their own stored value.
  useEffect(() => {
    if (!tenantId || axes.length === 0) {
      setSeeded(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const next: Record<string, string> = {};

    for (const axis of axes) {
      const fromUrl = params.get(axis.category.name.toLowerCase());
      const fromUrlTag = findScopeTag(axis, fromUrl);
      if (fromUrlTag) {
        next[axis.category.id] = fromUrlTag.id;
        writeStored(tenantId, axis.category.id, fromUrlTag.id);
        continue;
      }
      const stored = readStored(tenantId, axis.category.id);
      // A stored id for a tag that has since been renamed or deleted resolves
      // to nothing — drop it rather than pinning the student to a dead value.
      if (stored && findScopeTag(axis, stored)) next[axis.category.id] = stored;
    }
    setExplicit(next);
    setSeeded(true);
  }, [tenantId, axes]);

  // A tenant switch invalidates a signal read from the previous tenant's data.
  useEffect(() => {
    setInferred({});
    setSignalSettled(false);
    inferredFor.current = null;
  }, [tenantId]);

  const setScopeValue = useCallback(
    (categoryId: string, tag: Tag) => {
      setExplicit((prev) => ({ ...prev, [categoryId]: tag.id }));
      writeStored(tenantId, categoryId, tag.id);
      // An explicit choice ends inference for this session.
      inferredFor.current = tenantId;
    },
    [tenantId],
  );

  const absorbSearch = useCallback(
    (search: string) => {
      if (!tenantId || axes.length === 0 || !search) return;
      const params = new URLSearchParams(search);
      for (const axis of axes) {
        const tag = findScopeTag(axis, params.get(axis.category.name.toLowerCase()));
        if (tag) setScopeValue(axis.category.id, tag);
      }
    },
    [tenantId, axes, setScopeValue],
  );

  const noteEnrollmentSignal = useCallback(
    (courses: Course[], enrollments: Enrollment[]) => {
      if (!tenantId || axes.length === 0) return;
      // Consulted, whatever it says — a student with no enrolments has been
      // looked at too, and that is what lets the first-visit question appear.
      if (courses.length > 0) setSignalSettled(true);
      if (inferredFor.current === tenantId) return;
      if (enrollments.length === 0 || courses.length === 0) return;
      inferredFor.current = tenantId;

      const signals: Record<string, string> = {};
      for (const axis of axes) {
        const tag = inferScopeFromEnrollments(courses, enrollments, axis);
        if (tag) signals[axis.category.id] = tag.id;
      }
      // No need to skip axes the student has already chosen — an explicit
      // choice outranks this in resolveScopeValue, so writing it is harmless.
      if (Object.keys(signals).length > 0) setInferred(signals);
    },
    [tenantId, axes],
  );

  const selection = useMemo<ScopeSelection>(() => {
    const out: ScopeSelection = {};
    for (const axis of axes) {
      const chosen = resolveScopeValue(
        axis,
        explicit[axis.category.id],
        findScopeTag(axis, inferred[axis.category.id]),
      );
      if (chosen) out[axis.category.id] = chosen;
    }
    return out;
  }, [axes, explicit, inferred]);

  const undecidedAxes = useMemo<ScopeAxis[]>(() => {
    if (!seeded || !signalSettled) return [];
    return axes.filter(
      (axis) => !explicit[axis.category.id] && !findScopeTag(axis, inferred[axis.category.id]),
    );
  }, [axes, explicit, inferred, seeded, signalSettled]);

  const value = useMemo<ScopeContextValue>(
    () => ({
      axes,
      selection,
      isResolving: scoped && isLoading,
      undecidedAxes,
      setScopeValue,
      absorbSearch,
      noteEnrollmentSignal,
    }),
    [axes, selection, scoped, isLoading, undecidedAxes, setScopeValue, absorbSearch, noteEnrollmentSignal],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

export const useScope = () => useContext(ScopeContext);
